'use client'

/**
 * Single-elimination bracket tree (beta).
 *
 * - `BracketView` — the responsive tree: sticky round headers, rounds as
 *   columns, connector elbows drawn with CSS borders, horizontal snap-scroll
 *   on small screens (contained here so the page body never scrolls sideways).
 * - `MatchEditModal` — organizer-only winner entry, mirroring the RPC rules
 *   via isMatchEditable so we never offer an edit the server rejects.
 * - `BracketPageContent` — page wrapper: back link + fullscreen toggle, and
 *   the client-side organizer check (same as the manage page).
 * - `BracketPreviewSection` — event-page mini-map card linking to the page.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GpBadge } from '@/components/ui/gp-badge';
import Link from 'next/link'
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  Loader2,
  Maximize2,
  Minimize2,
  Trophy,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useCanManageTournament } from '@/lib/use-can-manage-tournament'
import {
  getBracket,
  setMatchResult,
  clearMatchResult,
  toMatchLike,
  type Bracket,
  type BracketMatch,
  type BracketPlayer,
} from '@/lib/tournament-bracket'
import { bracketProgress, isMatchEditable, roundLabel } from '@/lib/bracket-math'

/** Vertical space reserved per round-1 match; later rounds scale off this. */
const BAND_PX = 108

export function BracketView({
  tournamentId,
  isOrganizer,
  fullscreen = false,
}: {
  tournamentId: string
  isOrganizer: boolean
  /** Rendered inside the fullscreen container — adjusts the sticky offset. */
  fullscreen?: boolean
}) {
  // undefined = loading, null = no bracket exists.
  const [data, setData] = useState<
    { bracket: Bracket; matches: BracketMatch[] } | null | undefined
  >(undefined)
  const [editing, setEditing] = useState<BracketMatch | null>(null)

  // The sticky header row lives outside the horizontal scroller (vertical
  // sticky can't escape an overflow container), so its scrollLeft is synced
  // to the tree's on every scroll instead.
  const headerScrollRef = useRef<HTMLDivElement>(null)

  const reload = useCallback(() => {
    getBracket(tournamentId).then(setData)
  }, [tournamentId])

  useEffect(() => {
    reload()
  }, [reload])

  const matchLikes = useMemo(
    () => (data ? data.matches.map(toMatchLike) : []),
    [data]
  )

  if (data === undefined) {
    return (
      <div className="space-y-3">
        <div className="h-16 animate-pulse rounded-xl bg-white/5" />
        <div className="h-64 animate-pulse rounded-xl bg-white/5" />
      </div>
    )
  }

  if (data === null) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/40 px-10 py-16 text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Trophy className="size-5" />
        </div>
        <p className="text-lg font-semibold">No bracket yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The organizer hasn&apos;t generated the bracket for this event.
        </p>
      </div>
    )
  }

  const { bracket, matches } = data
  const rounds = Array.from({ length: bracket.rounds }, (_, i) =>
    matches.filter((m) => m.round === i + 1)
  )
  const playerCount = rounds[0].reduce(
    (n, m) => n + (m.p1 ? 1 : 0) + (m.p2 ? 1 : 0),
    0
  )
  const final = rounds[bracket.rounds - 1]?.[0]
  const champion =
    bracket.status === 'completed' && final?.winnerUserId
      ? final.winnerUserId === final.p1?.userId
        ? final.p1
        : final.p2
      : null
  const treeHeight = rounds[0].length * BAND_PX

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        <span>Single elimination · {playerCount} players</span>
        {champion && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">
            <Trophy className="size-3.5" /> Champion: {champion.gamertag}
          </span>
        )}
        {isOrganizer && !champion && (
          <span className="text-xs">Tap a match to record its result.</span>
        )}
      </div>

      {/* Round headers: sticky against vertical scroll (below the fixed
          navbar, or the container edge in fullscreen), scrollLeft-synced
          with the tree since sticky can't live inside the scroller. */}
      <div
        className={cn(
          'sticky z-20 bg-background/80 backdrop-blur-sm',
          fullscreen ? 'top-0' : 'top-16'
        )}
      >
        <div ref={headerScrollRef} className="overflow-x-hidden">
          <div className="flex min-w-max gap-8 py-2">
            {rounds.map((_, i) => (
              <div
                key={i}
                className="w-64 shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {roundLabel(i + 1, bracket.rounds)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Horizontal scroll is contained here — the page body never scrolls. */}
      <div
        className="overflow-x-auto snap-x pb-4"
        onScroll={(e) => {
          if (headerScrollRef.current)
            headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft
        }}
      >
        <div className="flex min-w-max gap-8">
          {rounds.map((roundMatches, i) => {
            const round = i + 1
            return (
              <div key={round} className="snap-start flex w-64 shrink-0 flex-col">
                <div className="flex flex-col" style={{ minHeight: treeHeight }}>
                  {roundMatches.map((m) => (
                    <MatchBand
                      key={m.id}
                      match={m}
                      isFirstRound={round === 1}
                      isFinal={round === bracket.rounds}
                      editable={
                        isOrganizer &&
                        isMatchEditable(toMatchLike(m), matchLikes, bracket.rounds)
                      }
                      locked={
                        isOrganizer &&
                        !!m.winnerUserId &&
                        !m.isBye &&
                        !isMatchEditable(toMatchLike(m), matchLikes, bracket.rounds)
                      }
                      onEdit={() => setEditing(m)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {editing && (
        <MatchEditModal
          match={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}

/**
 * One match inside its equal-height band. Bands are `flex-1`, so a round with
 * half the matches gets bands twice as tall — cards auto-center against their
 * feeder pair. Connector elbows: an outgoing stub on the right, a half-height
 * vertical joiner that meets its sibling's at the band boundary, and an
 * incoming stub on the left of the next round's card.
 */
function MatchBand({
  match,
  isFirstRound,
  isFinal,
  editable,
  locked,
  onEdit,
}: {
  match: BracketMatch
  isFirstRound: boolean
  isFinal: boolean
  editable: boolean
  locked: boolean
  onEdit: () => void
}) {
  return (
    <div
      className={cn(
        'relative flex flex-1 items-center py-2',
        !isFinal &&
          'after:absolute after:right-[-1rem] after:top-1/2 after:h-px after:w-4 after:bg-border',
        !isFirstRound &&
          'before:absolute before:left-[-1rem] before:top-1/2 before:h-px before:w-4 before:bg-border'
      )}
    >
      {!isFinal && (
        <span
          className="absolute right-[-1rem] w-px bg-border"
          style={
            match.position % 2 === 0
              ? { top: '50%', bottom: 0 }
              : { top: 0, bottom: '50%' }
          }
        />
      )}
      <MatchCard
        match={match}
        isFinal={isFinal}
        editable={editable}
        locked={locked}
        onEdit={onEdit}
      />
    </div>
  )
}

function MatchCard({
  match,
  isFinal,
  editable,
  locked,
  onEdit,
}: {
  match: BracketMatch
  isFinal: boolean
  editable: boolean
  locked: boolean
  onEdit: () => void
}) {
  const card = (
    <div
      className={cn(
        'w-56 overflow-hidden rounded-xl border border-border bg-card divide-y divide-border transition-colors',
        editable && 'cursor-pointer hover:border-white/20'
      )}
      title={locked ? 'Clear the next round’s result first' : undefined}
    >
      <PlayerRow
        player={match.p1}
        won={!!match.winnerUserId && match.winnerUserId === match.p1?.userId}
        decided={!!match.winnerUserId}
        byeSide={false}
        showTrophy={isFinal}
      />
      <PlayerRow
        player={match.p2}
        won={!!match.winnerUserId && match.winnerUserId === match.p2?.userId}
        decided={!!match.winnerUserId}
        byeSide={match.isBye}
        showTrophy={isFinal}
      />
    </div>
  )

  if (!editable) return card
  return (
    <button type="button" onClick={onEdit} className="text-left" aria-label="Record match result">
      {card}
    </button>
  )
}

function PlayerRow({
  player,
  won,
  decided,
  byeSide,
  showTrophy,
}: {
  player: BracketPlayer | null
  won: boolean
  decided: boolean
  byeSide: boolean
  /** The cup only appears on the final — winning it means the tournament. */
  showTrophy: boolean
}) {
  if (!player) {
    return (
      <div className="flex h-10 items-center px-3 text-sm italic text-muted-foreground/60">
        {byeSide ? 'BYE' : 'TBD'}
      </div>
    )
  }
  return (
    <div
      className={cn(
        'flex h-10 items-center gap-2 px-3',
        won && 'bg-primary/10',
        decided && !won && 'opacity-60'
      )}
    >
      <UserAvatar url={player.avatar} alt={player.gamertag} size={24} />
      <span
        className={cn(
          'min-w-0 truncate text-sm',
          won ? 'font-semibold' : decided ? 'text-muted-foreground' : ''
        )}
      >
        {player.gamertag}
      </span>
      <GpBadge userId={player.userId} gamertag={player.gamertag} size={12} className="ml-1" />
      {won && showTrophy && <Trophy className="ml-auto size-3.5 text-primary" />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Organizer result entry
// ---------------------------------------------------------------------------

function MatchEditModal({
  match,
  onClose,
  onSaved,
}: {
  match: BracketMatch
  onClose: () => void
  onSaved: () => void
}) {
  const [winnerId, setWinnerId] = useState<string | null>(match.winnerUserId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (!winnerId || busy) return
    setBusy(true)
    setError(null)
    const res = await setMatchResult(match.id, winnerId)
    setBusy(false)
    if (res.ok) onSaved()
    else setError(res.error ?? 'Could not save the result.')
  }

  const clear = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    const res = await clearMatchResult(match.id)
    setBusy(false)
    if (res.ok) onSaved()
    else setError(res.error ?? 'Could not clear the result.')
  }

  const players = [match.p1!, match.p2!]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl border border-border bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">Match result</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick the winner of this match.
        </p>

        <div className="mt-5 space-y-2">
          {players.map((player) => {
            const isWinner = winnerId === player.userId
            return (
              <button
                type="button"
                key={player.userId}
                onClick={() => setWinnerId(player.userId)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors cursor-pointer',
                  isWinner
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-white/5'
                )}
              >
                <UserAvatar url={player.avatar} alt={player.gamertag} size={28} />
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="truncate text-sm font-medium">{player.gamertag}</span>
                  <GpBadge userId={player.userId} gamertag={player.gamertag} size={13} />
                </span>
                {isWinner && <Check className="size-4 shrink-0 text-primary" />}
              </button>
            )
          })}
        </div>

        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

        <div className="mt-5 flex items-center justify-between gap-3">
          {match.winnerUserId ? (
            <button
              onClick={clear}
              disabled={busy}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-red-400 disabled:opacity-50 cursor-pointer"
            >
              Clear result
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={save}
            disabled={!winnerId || busy}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
          >
            {busy && <Loader2 className="size-4 animate-spin" />} Save result
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page wrapper
// ---------------------------------------------------------------------------

/**
 * Page wrapper: resolves the viewer's organizer status client-side (same
 * check as the manage page), renders the back link + fullscreen toggle row,
 * and owns laptop fullscreen — the Fullscreen API on this container, with a
 * CSS overlay fallback for iPhones where element fullscreen isn't supported.
 */
export function BracketPageContent({
  tournamentId,
  organizerIds,
  source = null,
  slug,
  locale,
}: {
  tournamentId: string
  organizerIds: string[]
  /** Import source of the event; null = created on GamerPlug (staff may manage). */
  source?: string | null
  slug: string
  locale: string
}) {
  const manageable = useMemo(
    () => ({ organizers: organizerIds.map((id) => ({ id })), source }),
    [organizerIds, source]
  )
  const { canManage: isOrganizer } = useCanManageTournament(manageable)

  const containerRef = useRef<HTMLDivElement>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [overlayFallback, setOverlayFallback] = useState(false)

  const enterFullscreen = async () => {
    const el = containerRef.current
    if (!el) return
    type FsElement = HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> }
    try {
      await (el.requestFullscreen?.() ??
        (el as FsElement).webkitRequestFullscreen?.() ??
        Promise.reject())
      // State flips via the fullscreenchange listener below.
    } catch {
      setOverlayFallback(true)
      setFullscreen(true)
    }
  }

  const exitFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen()
    setOverlayFallback(false)
    setFullscreen(false)
  }

  // Keep state in sync with native fullscreen (Esc is handled by the browser).
  useEffect(() => {
    const onChange = () => {
      const active = document.fullscreenElement === containerRef.current
      if (active) setFullscreen(true)
      else if (!overlayFallback) setFullscreen(false)
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [overlayFallback])

  // Overlay fallback only: Escape exits, page behind stops scrolling.
  useEffect(() => {
    if (!overlayFallback) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitFullscreen()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayFallback])

  return (
    <div
      ref={containerRef}
      className={cn(
        fullscreen && 'overflow-y-auto bg-background p-4 sm:p-6',
        fullscreen && overlayFallback && 'fixed inset-0 z-50'
      )}
    >
      <div className="mb-5 flex items-center justify-between">
        <Link
          href={`/${locale}/tournaments/${slug}`}
          className="inline-flex items-center gap-1.5 font-space-mono text-xs uppercase tracking-wider text-white/40 transition-colors hover:text-white/80"
        >
          <ArrowLeft size={13} />
          Back to event
        </Link>
        <button
          type="button"
          onClick={() => (fullscreen ? exitFullscreen() : enterFullscreen())}
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          className="flex size-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground cursor-pointer"
        >
          {fullscreen ? (
            <Minimize2 className="size-4" />
          ) : (
            <Maximize2 className="size-4" />
          )}
        </button>
      </div>
      <BracketView
        tournamentId={tournamentId}
        isOrganizer={isOrganizer}
        fullscreen={fullscreen}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Event-page preview section (mini map)
// ---------------------------------------------------------------------------

/**
 * Compact bracket section for the public event page: a mini-map silhouette of
 * the tree (one tinted block per match, band-aligned like the real thing) and
 * a link to the full bracket page. Renders nothing while loading or when no
 * bracket exists.
 */
export function BracketPreviewSection({
  tournamentId,
  slug,
  locale,
}: {
  tournamentId: string
  slug: string
  locale: string
}) {
  const [data, setData] = useState<
    { bracket: Bracket; matches: BracketMatch[] } | null | undefined
  >(undefined)

  useEffect(() => {
    getBracket(tournamentId).then(setData)
  }, [tournamentId])

  if (!data) return null

  const { bracket, matches } = data
  const rounds = Array.from({ length: bracket.rounds }, (_, i) =>
    matches.filter((m) => m.round === i + 1)
  )
  const playerCount = rounds[0].reduce(
    (n, m) => n + (m.p1 ? 1 : 0) + (m.p2 ? 1 : 0),
    0
  )
  const progress = bracketProgress(matches.map(toMatchLike), bracket.rounds)
  const final = rounds[bracket.rounds - 1]?.[0]
  const champion =
    progress.complete && final?.winnerUserId
      ? final.winnerUserId === final.p1?.userId
        ? final.p1
        : final.p2
      : null

  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold">Bracket</h2>
      <Link
        href={`/${locale}/tournaments/${slug}/bracket`}
        className="group block rounded-2xl border border-border bg-card p-5 transition-colors hover:bg-white/5"
      >
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <BracketMiniMap rounds={rounds} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">
              Single elimination · {playerCount} players
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {champion ? (
                <span className="inline-flex items-center gap-1 font-semibold text-primary">
                  <Trophy className="size-3" /> Champion: {champion.gamertag}
                </span>
              ) : (
                `${roundLabel(progress.currentRound, bracket.rounds)} · ${
                  progress.pendingInRound
                } match${progress.pendingInRound === 1 ? '' : 'es'} pending`
              )}
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
              Open bracket
              <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </section>
  )
}

/**
 * The silhouette: rounds as columns of small blocks inside the same
 * equal-band geometry as the real tree, so blocks center against their
 * feeder pairs. Decided matches glow primary, waiting matches are dim.
 */
function BracketMiniMap({ rounds }: { rounds: BracketMatch[][] }) {
  const height = Math.min(120, Math.max(48, rounds[0].length * 12))
  return (
    <div className="flex shrink-0 items-stretch gap-2" style={{ height }}>
      {rounds.map((roundMatches, i) => (
        <div key={i} className="flex flex-col">
          {roundMatches.map((m) => (
            <span key={m.id} className="flex flex-1 items-center">
              <span
                className={cn(
                  'block h-1.5 w-5 rounded-full',
                  m.winnerUserId
                    ? 'bg-primary/70'
                    : m.p1 || m.p2
                      ? 'bg-white/25'
                      : 'bg-white/10'
                )}
              />
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}
